import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './ui/Icon';
import { Kbd } from './ui/Kbd';
import { useEditor } from '@/store/editor';
import { useAuth } from '@/lib/auth';

type Props = {
  projectName?: string;
};

export function TopBar({ projectName }: Props) {
  const aiAnalyzing = useEditor((s) => s.aiAnalyzing);
  const openCmdK = useEditor((s) => s.openCmdK);
  const { user, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header className="bg-bg-panel border-b border-border flex items-center px-4 gap-4 h-[52px] relative">
      <Link to="/projects" className="flex items-center gap-2.5 font-semibold text-[15px] hover:opacity-80 transition-opacity">
        <div className="w-7 h-7 rounded-[7px] flex items-center justify-center bg-gradient-to-br from-accent to-ai shadow">
          <Icon name="clapperboard" size={16} className="text-white" />
        </div>
        <span>topStudio</span>
      </Link>

      <div className="flex items-center gap-2.5 text-[13px] text-text-muted">
        <span className="text-text-subtle">/</span>
        <span>{projectName ?? 'Projekt'}</span>
        {aiAnalyzing ? (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[rgba(167,139,250,0.15)] border border-[rgba(167,139,250,0.25)] text-ai text-[12px] font-medium">
            <div className="w-1.5 h-1.5 rounded-full bg-ai animate-pulse" />
            AI analyserar innehall...
          </div>
        ) : (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.3)] text-green-500 text-[12px] font-medium">
            <Icon name="check-circle-2" size={14} />
            Klar
          </div>
        )}
      </div>

      <button
        onClick={openCmdK}
        className="flex-1 max-w-[560px] mx-auto flex items-center gap-2.5 px-3.5 py-1.5 rounded-lg bg-bg-elevated border border-border hover:border-border-strong hover:bg-bg-panel-hover transition-all cursor-text"
      >
        <Icon name="sparkles" size={16} className="text-ai" />
        <span className="flex-1 text-left text-text-subtle">Skriv kommando eller fraga AI...</span>
        <Kbd>Ctrl</Kbd>
        <Kbd>K</Kbd>
      </button>

      <div className="ml-auto flex items-center gap-1.5">
        <button className="btn btn-ghost" title="Version-historik">
          <Icon name="history" size={16} />
        </button>
        <button className="btn btn-ghost" title="Installningar">
          <Icon name="settings" size={16} />
        </button>
        <button className="btn btn-primary">
          <Icon name="share-2" size={14} />
          Dela
        </button>

        {user && (
          <div className="relative ml-1" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((s) => !s)}
              className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-ai text-white text-xs font-semibold flex items-center justify-center hover:opacity-90"
              title={user.email ?? 'Inloggad'}
            >
              {(user.email ?? '?').slice(0, 2).toUpperCase()}
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-10 w-56 bg-bg-elevated border border-border-strong rounded-lg shadow-2xl py-1 z-50">
                <div className="px-3 py-2 border-b border-border">
                  <div className="text-xs text-text-muted">Inloggad som</div>
                  <div className="text-sm truncate">{user.email}</div>
                </div>
                <button
                  onClick={signOut}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-bg-panel-hover flex items-center gap-2 text-text"
                >
                  <Icon name="log-out" size={14} />
                  Logga ut
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
